// Pages written after the move off WordPress: Markdown files in content/
// (turned into { slug, data, html } by scripts/build/content-plugin.js) and
// team members added to src/data/team.js. They take the same shape as the
// captured pages in content.json, so routing, prerendering, the sitemap and
// the link check treat them exactly like the originals.
import { caseStudyDetails } from "../data/results";
import { team } from "../data/team";

const load = (modules) =>
  Object.values(modules).map((module) => module.default);

const blogFiles = load(import.meta.glob("/content/blog/*.md", { eager: true }));
const jobFiles = load(import.meta.glob("/content/jobs/*.md", { eager: true }));

/** First sentence-ish of some HTML, for meta descriptions. */
const summary = (text = "", length = 155) => {
  const plain = text
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return plain.length > length
    ? plain.slice(0, length).replace(/\s+\S*$/, "") + "…"
    : plain;
};

/** Split rendered Markdown at its ## headings so posts get a contents list. */
function blocksFrom(html) {
  const blocks = [];
  for (const part of html.split(/(?=<h2[^>]*>)/)) {
    const heading = part.match(/^<h2[^>]*>([\s\S]*?)<\/h2>/);
    if (heading) blocks.push({ type: "h2", text: summary(heading[1], 200) });
    const rest = heading ? part.slice(heading[0].length) : part;
    if (rest.trim()) blocks.push({ type: "rich", html: rest });
  }
  return blocks;
}

const youTube = (url) => (url ? [url] : []);

const page = (fields) => ({
  cards: [],
  externalEmbeds: [],
  downloadLinks: [],
  socialLinks: [],
  ...fields,
});

/** Blog posts, newest first. */
export const blogPosts = blogFiles
  .map(({ slug, data, html }) =>
    page({
      path: `/${slug}/`,
      kind: "insight",
      title: data.title,
      heading: data.title,
      description: data.description,
      image: data.image,
      date: data.date,
      author: data.author,
      body: blocksFrom(html),
      externalEmbeds: youTube(data.video),
    }),
  )
  .sort((a, b) => b.date.localeCompare(a.date));

/** Job postings; `status: open` ones are listed on the Careers page. */
export const jobPostings = jobFiles.map(({ slug, data, html }) => ({
  ...data,
  slug,
  path: data.path || `/careers/${slug}/`,
  page: page({
    path: data.path || `/careers/${slug}/`,
    kind: "career",
    title: data.title,
    heading: data.title,
    description: data.summary,
    image: data.image,
    imageAlt: data.imageAlt,
    // Job sections are h3s inside one rich block, like the captured posting,
    // so their ### sub-headings shift down to h4 first.
    body: [
      {
        type: "rich",
        html: html
          .replace(/<(\/?)h3>/g, "<$1h4>")
          .replace(/<(\/?)h2>/g, "<$1h3>"),
      },
    ],
  }),
}));

/** Profile pages for roster members who joined after the old site. */
export const teamPages = (capturedPaths) =>
  team
    .filter((person) => !capturedPaths.has(person.path))
    .map((person) =>
      page({
        path: person.path,
        kind: "team",
        title: person.fullName || person.name,
        heading: person.fullName || person.name,
        description: summary(
          person.bio?.[0]?.text ||
            `${person.fullName || person.name}, ${person.title} at Xeratec.`,
        ),
        body: [],
      }),
    );

/** Pages for headline case studies (data/results.js) new since the old site. */
export const casePages = (capturedPaths) =>
  Object.entries(caseStudyDetails)
    .filter(([path]) => !capturedPaths.has(path))
    .map(([path, study]) =>
      page({
        path,
        kind: "case",
        title: study.story.title.replaceAll("*", ""),
        heading: study.company,
        description: summary(study.story.situation[0]),
        body: [],
      }),
    );

/** Every written page; these replace captured pages at the same path. */
export function writtenPages(capturedPaths) {
  return [
    ...blogPosts,
    ...jobPostings.map((job) => job.page),
    ...teamPages(capturedPaths),
    ...casePages(capturedPaths),
  ];
}
